import React, { useMemo } from "react";
import Field from "../../sections/shared/Field";
import EditorGroup from "../../sections/shared/EditorGroup";

function normalizeServiceTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

export default function ServiceDesignSection({
  editedSection,
  t,
  flags,
  onStyleChange,
}) {
  const tplKey = useMemo(
    () => normalizeServiceTemplate(editedSection?.template),
    [editedSection?.template]
  );

  const style = editedSection?.style || {};
  const isServiceSection = Boolean(flags?.isServiceSection);

  if (!isServiceSection) return null;

  return (
    <>
      <EditorGroup
        title={t("sectionModal.service.cards.title", "Service Cards")}
        subtitle={t(
          "sectionModal.service.cards.help",
          "Colors for cards, text, icons, badges, and service links"
        )}
        defaultOpen
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("sectionModal.service.cardBg", "Card Background")}
              type="color"
              value={style.serviceCardBg}
              onChange={(v) => onStyleChange("serviceCardBg", v)}
            />
            <Field
              label={t("sectionModal.service.cardBorder", "Card Border")}
              type="color"
              value={style.serviceCardBorder}
              onChange={(v) => onStyleChange("serviceCardBorder", v)}
            />
            <Field
              label={t("sectionModal.service.cardTitle", "Card Title Color")}
              type="color"
              value={style.serviceCardTitleColor}
              onChange={(v) => onStyleChange("serviceCardTitleColor", v)}
            />
            <Field
              label={t("sectionModal.service.cardDescription", "Card Description Color")}
              type="color"
              value={style.serviceCardDescriptionColor}
              onChange={(v) => onStyleChange("serviceCardDescriptionColor", v)}
            />
            <Field
              label={t("sectionModal.service.iconBg", "Icon Background")}
              type="color"
              value={style.serviceIconBg}
              onChange={(v) => onStyleChange("serviceIconBg", v)}
            />
            <Field
              label={t("sectionModal.service.iconColor", "Icon Color")}
              type="color"
              value={style.serviceIconColor}
              onChange={(v) => onStyleChange("serviceIconColor", v)}
            />
            <Field
              label={t("sectionModal.service.featureText", "Feature Text Color")}
              type="color"
              value={style.serviceFeatureTextColor}
              onChange={(v) => onStyleChange("serviceFeatureTextColor", v)}
            />
            <Field
              label={t("sectionModal.service.featureIcon", "Feature Icon Color")}
              type="color"
              value={style.serviceFeatureIconColor}
              onChange={(v) => onStyleChange("serviceFeatureIconColor", v)}
            />
            <Field
              label={t("sectionModal.service.linkColor", "Link Color")}
              type="color"
              value={style.serviceLinkColor}
              onChange={(v) => onStyleChange("serviceLinkColor", v)}
            />
            <Field
              label={t("sectionModal.service.mutedText", "Muted Text Color")}
              type="color"
              value={style.serviceMutedTextColor}
              onChange={(v) => onStyleChange("serviceMutedTextColor", v)}
            />
          </div>

          {tplKey === "template1" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t("sectionModal.service.badgeBg", "Badge Background")}
                type="color"
                value={style.serviceBadgeBg}
                onChange={(v) => onStyleChange("serviceBadgeBg", v)}
              />
              <Field
                label={t("sectionModal.service.badgeText", "Badge Text")}
                type="color"
                value={style.serviceBadgeTextColor}
                onChange={(v) => onStyleChange("serviceBadgeTextColor", v)}
              />
              <Field
                label={t("sectionModal.service.badgeBorder", "Badge Border")}
                type="color"
                value={style.serviceBadgeBorder}
                onChange={(v) => onStyleChange("serviceBadgeBorder", v)}
              />
            </div>
          )}
        </div>
      </EditorGroup>

      {tplKey === "template2" && (
        <EditorGroup
          title={t("sectionModal.service.template2.title", "Template 2")}
          subtitle={t(
            "sectionModal.service.template2.help",
            "Colors for carousel panel, meta labels, image overlay, arrows, and dots"
          )}
          defaultOpen={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("sectionModal.service.panelBg", "Panel Background")}
              type="color"
              value={style.servicePanelBg}
              onChange={(v) => onStyleChange("servicePanelBg", v)}
            />
            <Field
              label={t("sectionModal.service.panelBorder", "Panel Border")}
              type="color"
              value={style.servicePanelBorder}
              onChange={(v) => onStyleChange("servicePanelBorder", v)}
            />
            <Field
              label={t("sectionModal.service.metaText", "Meta Text Color")}
              type="color"
              value={style.serviceMetaTextColor}
              onChange={(v) => onStyleChange("serviceMetaTextColor", v)}
            />
            <Field
              label={t("sectionModal.service.imageOverlay", "Mobile Image Overlay")}
              type="color"
              value={style.serviceImageOverlayColor}
              onChange={(v) => onStyleChange("serviceImageOverlayColor", v)}
            />
            <Field
              label={t("sectionModal.service.navBtnBg", "Nav Button Background")}
              type="color"
              value={style.serviceNavBtnBg}
              onChange={(v) => onStyleChange("serviceNavBtnBg", v)}
            />
            <Field
              label={t("sectionModal.service.navBtnBorder", "Nav Button Border")}
              type="color"
              value={style.serviceNavBtnBorder}
              onChange={(v) => onStyleChange("serviceNavBtnBorder", v)}
            />
            <Field
              label={t("sectionModal.service.navBtnIcon", "Nav Button Icon")}
              type="color"
              value={style.serviceNavBtnIcon}
              onChange={(v) => onStyleChange("serviceNavBtnIcon", v)}
            />
            <Field
              label={t("sectionModal.service.dotActive", "Active Dot")}
              type="color"
              value={style.serviceDotActive}
              onChange={(v) => onStyleChange("serviceDotActive", v)}
            />
            <Field
              label={t("sectionModal.service.dotInactive", "Inactive Dot")}
              type="color"
              value={style.serviceDotInactive}
              onChange={(v) => onStyleChange("serviceDotInactive", v)}
            />
            <Field
              label={t("sectionModal.service.priceColor", "Price Color")}
              type="color"
              value={style.servicePriceColor}
              onChange={(v) => onStyleChange("servicePriceColor", v)}
            />
          </div>
        </EditorGroup>
      )}

      {tplKey === "template3" && (
        <EditorGroup
          title={t("sectionModal.service.template3.title", "Template 3")}
          subtitle={t(
            "sectionModal.service.template3.help",
            "Colors for pricing toggle, popular badge, highlighted plan, prices, and limitations"
          )}
          defaultOpen={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("sectionModal.service.toggleBg", "Billing Toggle Background")}
              type="color"
              value={style.servicePricingToggleBg}
              onChange={(v) => onStyleChange("servicePricingToggleBg", v)}
            />
            <Field
              label={t("sectionModal.service.toggleActiveBg", "Billing Toggle Active Background")}
              type="color"
              value={style.servicePricingToggleActiveBg}
              onChange={(v) => onStyleChange("servicePricingToggleActiveBg", v)}
            />
            <Field
              label={t("sectionModal.service.toggleActiveText", "Billing Toggle Active Text")}
              type="color"
              value={style.servicePricingToggleActiveText}
              onChange={(v) => onStyleChange("servicePricingToggleActiveText", v)}
            />
            <Field
              label={t("sectionModal.service.toggleInactiveText", "Billing Toggle Inactive Text")}
              type="color"
              value={style.servicePricingToggleInactiveText}
              onChange={(v) => onStyleChange("servicePricingToggleInactiveText", v)}
            />
            <Field
              label={t("sectionModal.service.popularBg", "Popular Badge Background")}
              type="color"
              value={style.servicePopularBadgeBg}
              onChange={(v) => onStyleChange("servicePopularBadgeBg", v)}
            />
            <Field
              label={t("sectionModal.service.popularText", "Popular Badge Text")}
              type="color"
              value={style.servicePopularBadgeText}
              onChange={(v) => onStyleChange("servicePopularBadgeText", v)}
            />
            <Field
              label={t("sectionModal.service.highlightedRing", "Highlighted Ring")}
              type="color"
              value={style.serviceHighlightedRing}
              onChange={(v) => onStyleChange("serviceHighlightedRing", v)}
            />
            <Field
              label={t("sectionModal.service.priceColor", "Price Color")}
              type="color"
              value={style.servicePriceColor}
              onChange={(v) => onStyleChange("servicePriceColor", v)}
            />
            <Field
              label={t("sectionModal.service.periodColor", "Period Color")}
              type="color"
              value={style.servicePeriodColor}
              onChange={(v) => onStyleChange("servicePeriodColor", v)}
            />
            <Field
              label={t("sectionModal.service.limitationsColor", "Limitations Color")}
              type="color"
              value={style.serviceLimitationsColor}
              onChange={(v) => onStyleChange("serviceLimitationsColor", v)}
            />
            <Field
              label={t("sectionModal.service.xIconColor", "Limitation Icon Color")}
              type="color"
              value={style.serviceXIconColor}
              onChange={(v) => onStyleChange("serviceXIconColor", v)}
            />
            <Field
              label={t("sectionModal.service.secondaryBtnBg", "Non-highlighted Button Background")}
              type="color"
              value={style.serviceSecondaryBtnBg}
              onChange={(v) => onStyleChange("serviceSecondaryBtnBg", v)}
            />
            <Field
              label={t("sectionModal.service.secondaryBtnText", "Non-highlighted Button Text")}
              type="color"
              value={style.serviceSecondaryBtnText}
              onChange={(v) => onStyleChange("serviceSecondaryBtnText", v)}
            />
            <Field
              label={t("sectionModal.service.secondaryBtnBorder", "Non-highlighted Button Border")}
              type="color"
              value={style.serviceSecondaryBtnBorder}
              onChange={(v) => onStyleChange("serviceSecondaryBtnBorder", v)}
            />
          </div>
        </EditorGroup>
      )}
    </>
  );
}